// Static copy shared between the visible page sections and the JSON-LD in
// page.tsx. The FAQ schema must match the rendered FAQ text exactly.

export type Faq = { answer: string; question: string };

export const FAQS: Faq[] = [
  {
    answer:
      'We estimate your annual electricity use from your monthly bill and your state’s average residential rate, then shape it across the year using your state’s real month-by-month usage pattern from EIA data, adjusted for your climate region and heating fuel. It’s a general estimate — your actual usage depends on your home and habits.',
    question: 'How do you estimate my energy use from my zip code and bill?',
  },
  {
    answer:
      'No. This is a simulation based on regional averages and typical equipment pricing, like a Tesla Powerwall for batteries. For numbers built around your home, enter your zip code to get estimates from installers in your area — they’ll design a system for your specific home.',
    question: 'Is this a real quote?',
  },
  {
    answer:
      'Self-use stores your extra solar power for the evening instead of selling it back at a lower rate. Arbitrage charges when grid power is cheap and powers your home when it’s expensive. Backup keeps the battery in reserve so your home stays powered through an outage.',
    question: 'What do the three battery modes mean?',
  },
  {
    answer:
      'It depends on what you heat with today. Replacing electric resistance heat or oil usually saves a lot. Replacing cheap natural gas can be closer to break-even on cost — though it still cuts emissions and adds efficient air conditioning.',
    question: 'Will a heat pump really lower my bills?',
  },
  {
    answer:
      'Often, yes. Many states and utilities offer rebates, tax credits, and other incentives that can meaningfully lower what you pay. This simulation shows the full price before any incentives, so your real cost could be lower — installers near you can tell you exactly which programs you qualify for, and lease or PPA options can still tap a federal credit through 2027.',
    question: 'Could incentives lower my cost?',
  },
  {
    answer:
      'Payback is how long the upfront investment takes to pay for itself through energy savings, assuming utility rates keep rising about 3% per year. After payback, the savings are yours.',
    question: 'How is the payback period calculated?',
  },
];

export const METHODOLOGY_INTRO =
  'This is a simulation, not a quote — a general picture of how electrifying your home could change your costs. It combines your state’s average electricity rate and real month-by-month usage pattern (EIA data), your zip code’s climate region, and national average equipment pricing, indexed to popular models like the Tesla Powerwall 3. To see numbers built around your actual home, enter your zip code above and get free estimates from installers near you.';

export const METHODOLOGY_NOTES: string[] = [
  'Monthly usage follows your state’s actual residential consumption pattern from the EIA-861M survey, adjusted toward a winter-peaked curve for homes that heat with electricity. Your zip code’s climate region sets how that energy splits between heating, cooling, and everything else.',
  'We start from the heating fuel most common in your region — natural gas in the North and on the coasts, heat pumps across the South and Southwest — so the default profile reflects whole-home energy, not just electricity. You can change your heating fuel under “Fine-tune your simulation.”',
  'Solar systems are sized to cover your estimated annual usage (3–12 kW) at $2.80 per watt, producing on your region’s seasonal sun pattern. Excess solar is credited at 75% of the retail rate.',
  'Battery savings assume a Tesla Powerwall 3 (13.5 kWh usable, ~$15,500 installed). Arbitrage mode approximates a typical time-of-use rate spread; backup mode prioritizes resilience over savings.',
  'Heat pump economics compare a COP-3 heat pump against your current heating fuel at national average prices ($1.45 per therm of gas). Insulation trims heating and cooling loads by about 15%.',
  'The price shown is before incentives. Many states and utilities offer rebates and tax credits that can lower your cost — installers can confirm which programs you qualify for.',
  'Utility rates are assumed to rise 3% annually when computing the payback period.',
];

export const SOLUTION_BLURBS: Record<string, string> = {
  battery: 'Store energy for outages or to use when rates are high.',
  heatPump: 'Heat and cool your home with one efficient system.',
  insulation: 'Seal in comfort and shrink heating and cooling waste.',
  smartHome: 'Smart thermostats and switches that trim the waste.',
  solar: 'Generate your own power and shrink your electric bill.',
};

export const SOLUTION_DETAILS: Record<string, string> = {
  battery: 'A Powerwall keeps your essentials running for about a day.',
  heatPump: 'One electric unit replaces your furnace and air conditioner.',
  insulation: 'It also lets the rest of your system run smaller and cheaper.',
  smartHome: 'Small automatic adjustments add up across the year.',
  solar: 'We size it to cover most of your yearly electricity use.',
};
